import { useCallback, useState, useEffect } from 'react';
import { AppCheckbox, AppModal, AppRemixIcon } from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import colors from '../../../../../../constants/colors';
import Translator from '../../../../../components/helpers/Translator';
import GGCLPlantRow from './GGCLPlantRow';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import GGCLPlantSelectedEvidence from './GGCLPlantSelectedEvidence';
import ResponsibleSelect from '../../../../../components/form/Selects/ResponsibleSelect';
import AppSpinner from '../../../../../components/AppSpinner';
import StateSelect from '../../../../../components/form/Selects/StateSelect';
import GGCLPlantSelectedCalendar from './GGCLPlantSelectedCalendar';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import _ from 'lodash';
import { path } from '@/routing/helpers/router';

export default function GGCLPlantSelectedBlock(props) {
  const { plantIndex, obligationID, onUnselectPlant } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const plantListTab = useSelector((s) => s.currentPage?.plantListTab);
  const plant = useSelector(
    (s) => s.currentPage?.plantListTab?.[obligationID]?.[plantIndex],
  );
  const obligationLevels = useSelector((s) => s.currentPage?.obligationLevels);
  const responsibles = useSelector(
    (s) => s.currentPage?.responsibles?.[plant?.plantObligationID],
  );
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions,
  );
  const [plantPermissions, setPlantPermissions] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [showResponsibleListModal, setShowResponsibleListModal] =
    useState(false);

  // > LOAD PERMISSIONS
  const loadPlantPermissions = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_globalLegalCompliance_evaluation_plant_permissions_get',
          ),
          {
            plantHex: plant.hex,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            setPlantPermissions(r.data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [plant],
  );

  useEffect(() => {
    loadPlantPermissions();
  }, [plant]);
  // < LOAD PERMISSIONS

  // > SET SINGLE STATE
  const setSingleState = useCallback(
    async (payload) => {
      setIsLoading(true);
      axios
        .post(
          axiosPath(
            'api_r_user_module_globalLegalCompliance_evaluation_setSingleState',
          ),
          {
            obligationID,
            plantHex: plant.hex,
            moduleBridgeHex,
            payload,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'plantListTab',
                {
                  ...plantListTab,
                  [obligationID]: plantListTab?.[obligationID].map(
                    (p, index) =>
                      plantIndex === index
                        ? {
                            ...p,
                            state: r.data?.plantObligation?.state,
                            customState: r.data?.plantObligation?.customState,
                            border: r.data?.plantObligation?.border,
                          }
                        : p,
                  ),
                },
                true,
              ),
            );
            dispatch(
              currentPageAction.change('obligationLevels', {
                ...obligationLevels,
                level3: {
                  ...obligationLevels?.level3,
                  [obligationID]: r.data?.obligation,
                },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false));
    },
    [plantListTab, plant, obligationID, moduleBridgeHex],
  );
  // < SET SINGLE STATE

  // > TOGGLE SINGLE CORPORATE OBLIGATION
  const toggleSingleCorporateObligation = useCallback(
    async (payload) => {
      setIsLoading(true);
      axios
        .post(
          axiosPath(
            'api_r_user_module_globalLegalCompliance_evaluation_toggleSingleCorporateObligation',
          ),
          {
            obligationID,
            plantHex: plant.hex,
            moduleBridgeHex,
            payload,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'plantListTab',
                {
                  ...plantListTab,
                  [obligationID]: plantListTab[obligationID].map((p, index) =>
                    plantIndex === index
                      ? {
                          ...p,
                          isCorporate: r.data?.isCorporate,
                        }
                      : p,
                  ),
                },
                true,
              ),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false));
    },
    [plantListTab, plant, obligationID, moduleBridgeHex],
  );
  // < TOGGLE SINGLE CORPORATE OBLIGATION

  // > LOAD RESPONSIBLES
  const loadResponsibles = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_globalLegalCompliance_evaluation_responsible_selected_list',
          ),
          {
            plantObligationID: plant?.plantObligationID,
            plantHex: plant.hex,
            moduleBridgeHex,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('responsibles', {
                [plant?.plantObligationID]: r.data,
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [plant, moduleBridgeHex],
  );

  useEffect(() => {
    if (
      permissions?.ggcl?.gclResponsibles &&
      plantPermissions?.gcl?.module?.plant?.gclResponsibles
    ) {
      loadResponsibles();
    }
  }, [permissions, plantPermissions]);
  // < LOAD RESPONSIBLES

  // > SET SINGLE RESPONSIBLES
  const setSingleResponsibleEdit = useCallback(
    async (payload) => {
      setIsLoading(true);
      axios
        .post(
          axiosPath(
            'api_r_user_module_globalLegalCompliance_evaluation_responsible_edit',
          ),
          {
            obligationID,
            plantHex: plant.hex,
            moduleBridgeHex,
            responsibles: payload,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('responsibles', {
                [plant?.plantObligationID]: r.data,
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false));
    },
    [plantListTab, plant, obligationID, moduleBridgeHex],
  );
  // < SET SINGLE RESPONSIBLES

  // > GO TO OBLIGATION
  const goToObligation = useCallback(async () => {
    setIsLoading(true);
    axios
      .post(
        axiosPath(
          'api_r_user_module_globalLegalCompliance_evaluation_goToObligation',
        ),
        {
          obligationID,
          plantHex: plant.hex,
          moduleBridgeHex,
        },
      )
      .then(({data}) => {
        if (isMounted.current) {
          navigate(
            path({
              name: 'user_module_legalCompliance_evaluation',
              params: {
                moduleBridgeHex: data.modulebridgeHex,
                plantHex: data.plantHex,
                page: 1,
                filtersHex: data.filtersHex,
              },
            }),
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setIsLoading(false));
  }, [plant, obligationID, moduleBridgeHex]);
  // < GO TO OBLIGATION

  return (
    <Container>
      <TopLine className="d-flex justify-content-between">
        <div
          className="ggcl-psb-back"
          onClick={() => onUnselectPlant(undefined)}
        >
          {isLoading ? (
            <div className="d-inline-block">
              <AppSpinner>
                <AppRemixIcon icon="loader-4" size={14} className="mr-1" />
              </AppSpinner>
            </div>
          ) : (
            <AppRemixIcon icon="arrow-go-back" className="mr-1" />
          )}
          <Translator
            file="module-globallegalCompliance"
            name="back.to.plant.list"
          />
        </div>
        <span
          className="btn btn-xs btn-info align-self-center mr-3"
          onClick={goToObligation}
        >
          <Translator
            file="module-globallegalCompliance"
            name="go.to.obligation"
          />
        </span>
      </TopLine>
      <GGCLPlantRow
        plantIndex={plantIndex}
        obligationID={obligationID}
        onSelectedPlant={() => {}}
      />
      <div className="container-fluid mt-3">
        <div className="row mb-2">
          <div
            className={`col-12 ${
              permissions?.ggcl?.calendar &&
              plantPermissions?.gcl?.module?.plant?.calendar &&
              plantPermissions?.gcl?.module?.plant?.calendar !== false
                ? 'col-lg-6'
                : ''
            }`}
          >
            {permissions?.ggcl?.states &&
              plantPermissions?.gcl?.module?.plant?.states && (
                <StateSelect
                  onHandleChange={setSingleState}
                  value={{
                    isCustom: !!plant?.customState,
                    id: plant?.customState ? plant?.customState : plant?.state,
                  }}
                  isMulti={false}
                  hasSelectOptions
                  plantHex={plant?.hex}
                  hideObsolete
                />
              )}
            {permissions?.ggcl?.gclResponsibles &&
              plantPermissions?.gcl?.module?.plant?.gclResponsibles && (
                <div className="d-flex">
                  <ResponsibleSelect
                    value={_.map(responsibles, 'value') ?? []}
                    onHandleChange={setSingleResponsibleEdit}
                    hasSearchBox
                    isClearable
                    onlyWithPlantAccess
                    hasSelectOptions
                    ggclPlantHex={plant?.hex}
                    apiCall="api_r_user_module_globalLegalCompliance_evaluation_responsible_list"
                  />
                  {responsibles?.length > 0 && (
                    <>
                      <ResponsibleListBtn
                        className="btn btn-outline-info btn-sm ml-1"
                        onClick={() => setShowResponsibleListModal(true)}
                      >
                        <Translator
                          file="module-globallegalCompliance"
                          name="check.responsibles"
                        />
                      </ResponsibleListBtn>
                      <AppModal
                        show={showResponsibleListModal}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="responsibles"
                          />,
                        )}
                        onClose={() => setShowResponsibleListModal(false)}
                      >
                        <table className="table">
                          <tbody>
                            {responsibles?.map((r) => (
                              <tr key={r.value}>
                                <td>{r.label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </AppModal>
                    </>
                  )}
                </div>
              )}
            {permissions?.global?.corporateObligations && (
              <AppCheckbox
                value={plant.isCorporate}
                onChange={(v) => toggleSingleCorporateObligation(v)}
                hasBorder
              >
                <AppRemixIcon icon="building-4" filled />
                <Translator
                  file="module-legalCompliance"
                  name="corporate.obligation"
                />
              </AppCheckbox>
            )}
          </div>
          {permissions?.ggcl?.calendar &&
            plantPermissions?.gcl?.module?.plant?.calendar &&
            plantPermissions?.gcl?.module?.plant?.calendar !== false && (
              <div
                className={`col-12 ${
                  (permissions?.ggcl?.states &&
                    plantPermissions?.gcl?.module?.plant?.states) ||
                  (permissions?.ggcl?.gclResponsibles &&
                    plantPermissions?.gcl?.module?.plant?.gclResponsibles) ||
                  permissions?.global?.corporateObligations
                    ? 'col-lg-6'
                    : ''
                }`}
              >
                <GGCLPlantSelectedCalendar
                  obligationID={obligationID}
                  plantIndex={plantIndex}
                />
              </div>
            )}
        </div>
        {permissions?.ggcl?.evidences &&
          plantPermissions?.gcl?.module?.plant?.evidences && (
            <GGCLPlantSelectedEvidence
              obligationID={obligationID}
              plantIndex={plantIndex}
              evidencesPermissions={plantPermissions}
            />
          )}
      </div>
    </Container>
  );
}

const Container = styled.div`
  min-height: 623px;
  overflow: auto;
`;

const TopLine = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.grey400};
  padding-bottom: 10px;
  .ggcl-psb-back {
    cursor: pointer;
    padding: 6px 0;
  }
`;

const ResponsibleListBtn = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;
